import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { Switch } from '@/contasis';
import { Tooltip } from '@/shared/ui';
import {
  obtenerCodigosDetraccionCompatiblesConImpuesto,
  validarCoherenciaCompleta,
} from '@/shared/catalogos-sunat';
import type { CodigoDetraccionTributaria, TipoProductoDetraccion } from '@/shared/catalogos-sunat';
import type { ProductFormData } from '@/modelos/productos/Producto';
import { storageService } from '@/utils/storage';
import type { CodigoDetraccionDto } from '@/modelos/Auth';

interface SeccionInformacionTributariaProductoProps {
  formData: ProductFormData;
  setFormData: Dispatch<SetStateAction<ProductFormData>>;
  error?: string;
  productType?: TipoProductoDetraccion;
  impuesto?: string;
}

function dtoATributaria(dto: CodigoDetraccionDto): CodigoDetraccionTributaria {
  const afectaciones = dto.afectacionesCompatibles
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is 'gravado' | 'exonerado' => s === 'gravado' || s === 'exonerado');

  return {
    catalogo: '54',
    codigo: dto.codigo,
    descripcion: dto.descripcion,
    visible: dto.esVisible,
    tipoOperacionRelacionado: dto.tipoOperacionRelacionado,
    clasificacion: dto.clasificacion,
    porcentajeNormativo: dto.porcentajeNormativo,
    tipoPorcentaje: dto.tipoPorcentaje as CodigoDetraccionTributaria['tipoPorcentaje'],
    notaPorcentaje: dto.notaPorcentaje ?? undefined,
    afectacionesCompatibles: afectaciones,
    implementacion: 'implementado',
  };
}

function etiquetaPorcentaje(item: CodigoDetraccionTributaria): string {
  if (item.tipoPorcentaje === 'fijo' && item.porcentajeNormativo !== null) {
    return `${item.porcentajeNormativo}%`;
  }
  if (item.tipoPorcentaje === 'condicional') return 'Condicional';
  if (item.tipoPorcentaje === 'variable') return 'Variable';
  return 'Pendiente';
}

function etiquetaOpcion(item: CodigoDetraccionTributaria): string {
  return `${item.codigo} - ${item.descripcion} - ${etiquetaPorcentaje(item)}`;
}

export function SeccionInformacionTributariaProducto({
  formData,
  setFormData,
  error,
  productType = 'BIEN',
  impuesto = '',
}: SeccionInformacionTributariaProductoProps) {
  const tieneDetraccion = formData.sujetoDetraccion ?? false;

  const [textoInput, setTextoInput] = useState('');
  const [comboboxAbierto, setComboboxAbierto] = useState(false);
  const [rectInput, setRectInput] = useState<DOMRect | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const contenedorInputRef = useRef<HTMLDivElement>(null);
  const dropdownPortalRef = useRef<HTMLDivElement | null>(null);

  const catalogoFromStorage = useMemo<CodigoDetraccionTributaria[]>(
    () => storageService.getCodigosDetraccion().map(dtoATributaria),
    [],
  );

  const detalle = useMemo(
    () => catalogoFromStorage.find((i) => i.codigo === formData.detraccionCodigo),
    [catalogoFromStorage, formData.detraccionCodigo],
  );

  const modoSeleccionado = !comboboxAbierto && !!detalle;

  useEffect(() => {
    setTextoInput(detalle ? etiquetaOpcion(detalle) : '');
  }, [detalle]);

  useEffect(() => {
    const handleClickFuera = (e: MouseEvent) => {
      const target = e.target as Node;
      const dentroInput = contenedorInputRef.current?.contains(target) ?? false;
      const dentroDropdown = dropdownPortalRef.current?.contains(target) ?? false;
      if (!dentroInput && !dentroDropdown) {
        setComboboxAbierto(false);
        setTextoInput(detalle ? etiquetaOpcion(detalle) : '');
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, [detalle]);

  useEffect(() => {
    if (!comboboxAbierto) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setComboboxAbierto(false);
        setTextoInput(detalle ? etiquetaOpcion(detalle) : '');
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [comboboxAbierto, detalle]);

  useEffect(() => {
    if (!comboboxAbierto) return;
    const handleScroll = (e: Event) => {
      const dentroDropdown = dropdownPortalRef.current?.contains(e.target as Node) ?? false;
      if (!dentroDropdown) {
        setComboboxAbierto(false);
        setTextoInput(detalle ? etiquetaOpcion(detalle) : '');
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [comboboxAbierto, detalle]);

  const actualizarRect = useCallback(() => {
    if (inputRef.current) {
      setRectInput(inputRef.current.getBoundingClientRect());
    }
  }, []);

  useEffect(() => {
    if (!formData.sujetoDetraccion || !formData.detraccionCodigo) return;
    const coherencia = validarCoherenciaCompleta({
      codigoCat54: formData.detraccionCodigo,
      tipoProducto: productType,
      impuesto,
    });
    if (!coherencia.valido && coherencia.esBloqueo) {
      setFormData((prev) => ({
        ...prev,
        detraccionCodigo: null,
        detraccionId: null,
        detraccionNombre: null,
        detraccionPorcentaje: null,
      }));
      setTextoInput('');
      setComboboxAbierto(false);
    }
  }, [productType, impuesto, formData.sujetoDetraccion, formData.detraccionCodigo, setFormData]);

  const codigosCompatibles = useMemo(
    () => obtenerCodigosDetraccionCompatiblesConImpuesto(productType, impuesto, catalogoFromStorage),
    [productType, impuesto, catalogoFromStorage],
  );

  const codigosFiltrados = useMemo(() => {
    const termino = textoInput.toLowerCase().trim();
    if (!termino || modoSeleccionado) return codigosCompatibles;
    return codigosCompatibles.filter(
      (item) =>
        item.codigo.includes(termino) ||
        item.descripcion.toLowerCase().includes(termino) ||
        item.clasificacion.toLowerCase().includes(termino) ||
        item.tipoPorcentaje.toLowerCase().includes(termino) ||
        (item.porcentajeNormativo !== null && String(item.porcentajeNormativo).includes(termino)),
    );
  }, [textoInput, modoSeleccionado, codigosCompatibles]);

  const seleccionarCodigo = (item: CodigoDetraccionTributaria) => {
    const dtoOriginal = storageService.getCodigosDetraccion().find((d) => d.codigo === item.codigo);
    setFormData((prev) => ({
      ...prev,
      detraccionCodigo: item.codigo,
      detraccionId: dtoOriginal?.id ?? null,
      detraccionNombre: item.descripcion,
      detraccionPorcentaje: item.porcentajeNormativo,
    }));
    setTextoInput(etiquetaOpcion(item));
    setComboboxAbierto(false);
  };

  const handleCambioInput = (valor: string) => {
    setTextoInput(valor);
    setComboboxAbierto(true);
    if (detalle && valor !== etiquetaOpcion(detalle)) {
      setFormData((prev) => ({
        ...prev,
        detraccionCodigo: null,
        detraccionId: null,
        detraccionNombre: null,
        detraccionPorcentaje: null,
      }));
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-sm text-gray-700">Aplica detracción</span>
          <Tooltip
            text="Marca esta opción si este producto o servicio puede estar sujeto a detracción."
            position="top"
          >
            <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
          </Tooltip>
          <span className="text-xs text-gray-400">{tieneDetraccion ? 'Sí' : 'No'}</span>
          <Switch
            checked={tieneDetraccion}
            onChange={(checked) => {
              setFormData((prev) => ({
                ...prev,
                sujetoDetraccion: checked,
                detraccionCodigo: checked ? prev.detraccionCodigo : null,
                detraccionId: checked ? prev.detraccionId : null,
                detraccionNombre: checked ? prev.detraccionNombre : null,
                detraccionPorcentaje: checked ? prev.detraccionPorcentaje : null,
              }));
              if (!checked) {
                setTextoInput('');
                setComboboxAbierto(false);
              }
            }}
          />
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-sm text-gray-700">Aplica ICBPER</span>
          <Tooltip
            text="Marca esta opción si el producto es una bolsa de plástico sujeta al Impuesto al Consumo de Bolsas de Plástico (ICBPER)."
            position="top"
          >
            <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
          </Tooltip>
          <span className="text-xs text-gray-400">{formData.esIcbper ? 'Sí' : 'No'}</span>
          <Switch
            checked={formData.esIcbper ?? false}
            onChange={(checked) => setFormData((prev) => ({ ...prev, esIcbper: checked }))}
          />
        </div>

        {tieneDetraccion && (
          <div ref={contenedorInputRef} className="flex-1 min-w-52">
            {codigosCompatibles.length === 0 && !formData.detraccionCodigo ? (
              <p className="text-xs text-amber-600 py-1">
                No hay códigos de detracción habilitados para esta afectación.
              </p>
            ) : (
              <input
                ref={inputRef}
                type="text"
                placeholder="Seleccionar o buscar código de detracción"
                value={textoInput}
                onChange={(e) => handleCambioInput(e.target.value)}
                onFocus={() => {
                  actualizarRect();
                  if (detalle) setTextoInput('');
                  setComboboxAbierto(true);
                }}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  error
                    ? 'border-red-400 bg-white'
                    : modoSeleccionado
                      ? 'border-gray-300 bg-gray-50 text-gray-900 cursor-pointer'
                      : 'border-gray-300 bg-white text-gray-600'
                }`}
              />
            )}

            {comboboxAbierto && rectInput && codigosCompatibles.length > 0 &&
              createPortal(
                <div
                  ref={dropdownPortalRef}
                  style={{
                    position: 'fixed',
                    top: rectInput.bottom + 4,
                    left: rectInput.left,
                    width: rectInput.width,
                    zIndex: 9999,
                  }}
                  className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-lg"
                >
                  {codigosFiltrados.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400">Sin resultados para esta búsqueda</div>
                  ) : (
                    codigosFiltrados.map((item) => (
                      <button
                        key={item.codigo}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          seleccionarCodigo(item);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 transition-colors"
                      >
                        <span className="font-mono text-xs font-medium text-gray-900 w-7 shrink-0">
                          {item.codigo}
                        </span>
                        <span className="text-xs text-gray-700 flex-1 truncate">
                          {item.descripcion}
                        </span>
                        <span
                          className={`text-xs font-medium shrink-0 ${
                            item.tipoPorcentaje === 'fijo'
                              ? 'text-gray-600'
                              : item.tipoPorcentaje === 'condicional'
                                ? 'text-amber-600'
                                : 'text-gray-400'
                          }`}
                        >
                          {etiquetaPorcentaje(item)}
                        </span>
                      </button>
                    ))
                  )}
                </div>,
                document.body
              )
            }
          </div>
        )}
      </div>

      {error && tieneDetraccion && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}


